"use client";

import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import { ZoneLink } from "@/components/layout/zone-link";
import { useSession } from "@/lib/auth/client";

export type HuntSparkSurface = "job_detail" | "home" | "feed";

const COPY: Record<
	HuntSparkSurface,
	{ en: { title: string; cta: string }; id: { title: string; cta: string } }
> = {
	job_detail: {
		en: {
			title: "Find more roles like this — no signup",
			cta: "Match me with AI",
		},
		id: {
			title: "Cari lowongan lain seperti ini — tanpa daftar",
			cta: "Cocokkan dengan AI",
		},
	},
	feed: {
		en: { title: "Stop scrolling — let AI match you", cta: "Match me with AI" },
		id: {
			title: "Berhenti scroll — biar AI yang cocokkan",
			cta: "Cocokkan dengan AI",
		},
	},
	home: {
		en: {
			title: "Tell me your skills, I'll hunt jobs for you",
			cta: "Start hunting — free",
		},
		id: {
			title: "Sebutkan skill kamu, saya carikan lowongan",
			cta: "Mulai berburu — gratis",
		},
	},
};

/**
 * Discovery entry for anonymous Hunt (PR-D). A context-aware CTA placed on the
 * surfaces where traffic lands (job detail, feed, homepage) that drops a
 * logged-out visitor straight into the anon Hunt at `/seeker`. Rendered ONLY
 * when the `hunt-anon` flag is on (server passes `enabled`) and the viewer is
 * not signed in — otherwise null, so it never sends anyone into a login wall.
 * The 24h anon session gives natural continuity: clicking from any surface
 * resumes the same Hunt, so it's never redundant.
 */
export function HuntSpark({
	locale,
	surface,
}: {
	locale: "en" | "id";
	surface: HuntSparkSurface;
}) {
	const posthog = usePostHog();
	// Client-side flag eval — no per-surface server plumbing, ISR-safe. The
	// flag is the sole switch (default off ⇒ undefined/false ⇒ inert).
	const enabled = useFeatureFlagEnabled("hunt-anon");
	const { data: session } = useSession();
	// Signed-in users already have Hunt at /seeker — the spark is anon-only.
	if (!enabled || session?.user) return null;

	const copy = COPY[surface][locale];

	return (
		<ZoneLink
			href={`/${locale}/seeker`}
			onClick={() => {
				posthog?.capture("hunt_spark_clicked", { surface, is_anon: true });
			}}
			className="group flex items-center justify-between gap-3 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 transition-colors hover:border-primary/60 hover:bg-primary/10"
			data-testid="hunt-spark"
			data-surface={surface}
		>
			<span className="flex items-center gap-2.5 text-sm font-medium text-foreground">
				{copy.title}
			</span>
			<span className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors group-hover:bg-primary/90">
				{copy.cta}
			</span>
		</ZoneLink>
	);
}
